import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Brightness1Icon from "@mui/icons-material/Brightness1";
import Button from "@mui/material/Button";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LinearProgress from "@mui/material/LinearProgress";
import Swiper from "../../../../components/Swiper";

const autoPlayInterval = 10000;
function GettingStartedSlider({ steps }) {
  const _now = Date.now();
  const [index, setIndex] = useState(0);
  const [waitTime, setWaitTime] = useState(0);
  const [progressTo, setProgressTo] = useState(0); // 0 [middle values] 100
  const [startTime, setStartTime] = useState(_now);
  const [timeLeft, setTimeLeft] = useState(autoPlayInterval);
  const [interactionStartTime, setInteractionStartTime] = useState(_now); // onProgressStop
  const swiperRef = useRef(null);

  const updateSwiperIndex = (nextIndex) => {
    if (swiperRef.current) swiperRef.current.slideTo(+nextIndex);
  };

  const resetProgress = (value = 0) => {
    const now = Date.now();
    setProgressTo(value);
    setWaitTime(0);
    setStartTime(now);
    setTimeLeft(autoPlayInterval);
    setInteractionStartTime(now);
  };
  const handleChangeIndex = (i) => {
    let newIndex = i;
    if (i < 0) newIndex = 0;
    if (i > steps.length) newIndex = steps.length;
    resetProgress();
    setIndex(newIndex);
    updateSwiperIndex(newIndex);
  };
  const handleNext = () => {
    resetProgress();
    setIndex((prevActiveStep) => {
      let newIndex = prevActiveStep + 1;
      if (prevActiveStep === steps.length - 1) newIndex = 0;
      setTimeout(() => updateSwiperIndex(newIndex), 0);
      return newIndex;
    });
  };
  const handleBack = () => {
    resetProgress();
    setIndex((prevActiveStep) => {
      let newIndex = prevActiveStep - 1;
      if (prevActiveStep === 0) newIndex = steps.length - 1;
      setTimeout(() => updateSwiperIndex(newIndex), 0);
      return newIndex;
    });
  };
  const startAutoPlay = () => {
    const now = Date.now();
    const timeDifference = now - interactionStartTime;
    const totalWaitTime = waitTime + timeDifference;
    const progressRunTime = now - (startTime - totalWaitTime);
    const newTimeLeft = autoPlayInterval - progressRunTime;
    setProgressTo(100);
    setWaitTime(totalWaitTime);
    setTimeLeft(newTimeLeft < 0 ? 0 : newTimeLeft);
  };
  const stopAutoPlay = () => {
    const now = Date.now();
    const progressRunTime = now - (startTime - waitTime);
    const progressPercent = Math.round(
      (progressRunTime / autoPlayInterval) * 100
    );
    setProgressTo(progressPercent);
    setInteractionStartTime(now);
    setTimeLeft(null);
  };

  useEffect(() => {
    let timer = null;
    if (typeof timeLeft !== "object") {
      timer = setTimeout(handleNext, timeLeft);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, timeLeft]);

  useEffect(() => {
    resetProgress();
    const timer = setTimeout(() => {
      resetProgress(100);
    }, 0);

    return () => {
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  return (
    <Box
      sx={{
        "& .MuiLinearProgress-bar": {
          transition:
            progressTo <= 0 || !timeLeft
              ? "none"
              : `transform ${timeLeft}ms linear`,
        },
      }}
    >
      <LinearProgress
        sx={{ top: "-0.8em" }}
        variant="determinate"
        value={progressTo}
      />
      <Swiper
        swiperRef={swiperRef}
        onIndexChange={(nextIndex) => handleChangeIndex(nextIndex)}
      >
        {steps.map((step) => (
          <Box
            sx={{ maxHeight: "25rem ", cursor: "pointer" }}
            key={step.id}
            onMouseEnter={stopAutoPlay}
            onMouseLeave={startAutoPlay}
          >
            {step.description}
          </Box>
        ))}
      </Swiper>
      <Box style={{ display: "flex", justifyContent: "space-between" }}>
        <Box>
          <Button size="small" onClick={handleBack} disabled={index === 0}>
            <KeyboardArrowLeft />
            Back
          </Button>
        </Box>
        <Box
          sx={(theme) => ({
            [theme.breakpoints.down("sm")]: {
              display: "none",
            },
          })}
        >
          {steps.map((step) => (
            <IconButton
              key={step.id}
              aria-label={`Go to slide ${step.id + 1}`}
              onClick={() => {
                handleChangeIndex(step.id);
              }}
            >
              <Brightness1Icon
                sx={[
                  { fontSize: "1rem" },
                  index === step.id ? { color: "#68C565" } : { color: "grey" },
                ]}
              />
            </IconButton>
          ))}
        </Box>
        <Box>
          <Button
            size="small"
            onClick={handleNext}
            disabled={steps.length - 1 === index}
          >
            Next
            <KeyboardArrowRight />
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

GettingStartedSlider.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      description: PropTypes.element.isRequired,
    })
  ).isRequired,
};

export default GettingStartedSlider;
